/**
 * The sample café's frame (owner, 2026-10-12, demo option (a); lib/demoTour.ts has the rules): the guided tour's bar above the
 * screens, the view cap, and the photo/AI lock. Wraps the signed-in app. Only the sample counts (isDemoSample): a real business renders
 * its children untouched, with no listener at all.
 *
 * The view cap REPLACES the 18-tap gate (owner, 2026-10-05: "fade out after 18 clicks"): an 8-screen tour is Next and Back taps, so a
 * tap count would close the tour half-way. Now content is counted, not taps: DEMO_VIEW_CAP content views a session (a guide, a log, the
 * binder, records, an Ask answer; menus, tabs and Back don't count). The next one past the cap shows the same two choices as every
 * tour screen ("Use it free for 25 days", or see the other side), with "Go back" as the open door. What they tried is kept and said
 * (lib/demoCarry.ts), never claimed when nothing was tried.
 */
import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { useSessionState } from "../session.tsx";
import { isDemoSample } from "../lib/demoAsk.ts";
import { prefersReducedMotion } from "../lib/motion.ts";
import { DEMO_ONLY_EVENT } from "../lib/demoOnly.ts";
import { announceLock, contentView, guardDemoPhotos, takeView, useLock } from "../lib/demoTour.ts";

export function DemoTapGate({ children }: { children: ReactNode }) {
  const s = useSessionState();
  const sample = isDemoSample(s.org);
  const lock = useLock();
  const gated = sample && lock === "cap";
  const loc = useLocation();
  const navType = useNavigationType();

  // The view cap: each new screen that is content counts once (its own navigation key); Back (a POP, or the tour's Back) never does.
  // A tab (the same screen with another query or hash) is the screen before it, so it doesn't count either.
  const counted = useRef<string | null>(null);
  const before = useRef<string | null>(null);
  useEffect(() => {
    if (!sample || counted.current === loc.key) return;
    counted.current = loc.key;
    const view = contentView(loc.pathname, loc.search);
    const same = view !== null && view === before.current;
    before.current = view;
    if (same || navType === "POP" || (loc.state as { demoBack?: boolean } | null)?.demoBack) return;
    if (view && takeView(view) === "capped") announceLock("cap");
  }, [sample, loc.key]); // eslint-disable-line react-hooks/exhaustive-deps

  // Photos and the camera: the lock instead (and links to the API: the demo-only card).
  const [only, setOnly] = useState<string>();
  useEffect(() => {
    if (!sample) return;
    const on = (e: Event) => setOnly((e as CustomEvent<{ from: string }>).detail?.from ?? "");
    window.addEventListener(DEMO_ONLY_EVENT, on);
    let undo = () => {}, live = true;
    const undoPhotos = guardDemoPhotos();
    void import("../lib/demoOnlyGuard.ts").then((m) => { if (live) undo = m.guardDemoLinks(); });
    return () => { live = false; window.removeEventListener(DEMO_ONLY_EVENT, on); undo(); undoPhotos(); };
  }, [sample]);

  if (!sample) return <>{children}</>;
  const reduced = prefersReducedMotion();
  return (
    <>
      <Suspense fallback={null}><DemoTourHost where="bar" /></Suspense>
      <div className="demo-stage" data-testid="demo-stage" data-gated={gated ? "" : undefined} {...(gated ? { inert: true, "aria-hidden": true } : {})}
        style={{ opacity: gated ? 0.12 : 1, transition: reduced ? "none" : "opacity var(--dur-slow) var(--ease-out)" }}>
        {children}
      </div>
      <Suspense fallback={null}><DemoTourHost where="card" /></Suspense>
      {!lock && only !== undefined && <Suspense fallback={null}><DemoOnlyCard from={only} state={s.org?.state} onClose={() => setOnly(undefined)} /></Suspense>}
    </>
  );
}

const DemoTourHost = lazy(() => import("./DemoTour.tsx"));
const DemoOnlyCard = lazy(() => import("./DemoOnlyCard.tsx"));
